/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Table, TableContainer } from '@chakra-ui/react'
import {
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import TableHeader from './components/TableHeader'
import TableBody from './components/TableBody'

const parseVisibilityFromParam = (param, optionalIds) => {
  const visibleIds = new Set(param.split(',').filter(Boolean))
  return Object.fromEntries(optionalIds.map(id => [id, visibleIds.has(id)]))
}

const buildParamFromVisibility = (columnVisibility, optionalIds) => {
  const visibleIds = optionalIds.filter(id => columnVisibility[id] !== false)
  return visibleIds.join(',')
}

const DefaultTableHidingColumns = ({
  data,
  columns,
  getItemProps,
  tableInitialState = {},
  customTableBody: CustomTableBody,
  visibleColumnsParamKey
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const optionalIds = columns
    .filter(col => col.enableHiding !== false)
    .map(col => col.id ?? col.accessorKey)
  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (visibleColumnsParamKey) {
      const param = searchParams.get(visibleColumnsParamKey)

      if (param !== null) return parseVisibilityFromParam(param, optionalIds)
    }
    return tableInitialState?.columnVisibility
  })
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      return
    }
    if (!visibleColumnsParamKey) return
    const param = buildParamFromVisibility(columnVisibility, optionalIds)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      if (param) {
        next.set(visibleColumnsParamKey, param)
      } else {
        next.delete(visibleColumnsParamKey)
      }
      return next
    }, { replace: true })
  }, [columnVisibility])

  const table = useReactTable({
    data,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel()
  })

  return (
    <section>
      <TableContainer>
        <Table
          variant="simple"
          className="whitespace-normal"
        >
          <TableHeader
            table={table}
            setColumnVisibility={setColumnVisibility}
          />
          { CustomTableBody
            ? <CustomTableBody
                table={table}
                getItemProps={getItemProps}
              />
            : <TableBody
                table={table}
                getItemProps={getItemProps}
              />
          }
        </Table>
      </TableContainer>
    </section>
  )
}

export default DefaultTableHidingColumns
